import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchOrgUnitTree } from '../features/orgUnits/orgUnitsApi';
import type { OrgUnitNode } from '../features/orgUnits/types';
import { RiskHeatmap } from '../features/risks/RiskHeatmap';
import { EvaluationThresholdsPanel } from '../features/risks/EvaluationThresholdsPanel';
import {
  fetchEvaluationMethods,
  fetchRiskDashboard,
} from '../features/risks/risksApi';
import type {
  EvaluationMethod,
  RiskDashboardData,
  RiskKind,
  RiskScope,
  RiskStatus,
} from '../features/risks/types';
import { RISK_KIND_LABEL, RISK_STATUS_LABEL } from '../features/risks/riskUtils';

const SCOPES: RiskScope[] = ['estrategico', 'operativo'];
const KINDS: RiskKind[] = ['riesgo', 'oportunidad'];
const STATUSES: RiskStatus[] = [
  'identificado',
  'evaluado',
  'en_tratamiento',
  'monitoreado',
  'cerrado',
];

const SCOPE_LABEL: Record<RiskScope, string> = {
  estrategico: 'Estratégico',
  operativo: 'Operativo (IPER)',
};

function flattenOrgUnits(nodes: OrgUnitNode[]): OrgUnitNode[] {
  const result: OrgUnitNode[] = [];
  for (const node of nodes) {
    result.push(node);
    result.push(...flattenOrgUnits(node.children));
  }
  return result;
}

export function RiskDashboardPage() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<RiskDashboardData | null>(null);
  const [methods, setMethods] = useState<EvaluationMethod[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scope, setScope] = useState('');
  const [methodId, setMethodId] = useState('');
  const [residual, setResidual] = useState(false);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [orgUnitId, setOrgUnitId] = useState('');
  const [promotionThreshold, setPromotionThreshold] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dashboard = await fetchRiskDashboard({
        scope: (scope as RiskScope) || undefined,
        methodId: methodId || undefined,
        residual: residual || undefined,
        kind: (kind as RiskKind) || undefined,
        status: (status as RiskStatus) || undefined,
        orgUnitId: orgUnitId || undefined,
        promotionThreshold,
      });
      setData(dashboard);
    } catch {
      setError('No se pudo cargar el tablero de riesgos.');
    } finally {
      setLoading(false);
    }
  }, [scope, methodId, residual, kind, status, orgUnitId, promotionThreshold]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const unitFromQuery = searchParams.get('orgUnitId');
    if (unitFromQuery) setOrgUnitId(unitFromQuery);
  }, [searchParams]);

  useEffect(() => {
    void fetchEvaluationMethods().then(setMethods).catch(() => undefined);
    void fetchOrgUnitTree().then(setOrgUnits).catch(() => undefined);
  }, []);

  const flatUnits = flattenOrgUnits(orgUnits);

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold text-foreground'>Tablero de riesgos</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Mapa de calor, KPIs, estructura IPER, contexto y comparativo inicial/residual (MAP).
        </p>
      </div>

      <div className='flex flex-wrap gap-2 rounded-lg border bg-card p-4'>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className='border rounded-md px-3 py-2 text-sm'
          aria-label='Alcance'
        >
          <option value=''>Todos los alcances</option>
          {SCOPES.map((s) => (
            <option key={s} value={s}>
              {SCOPE_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={methodId}
          onChange={(e) => setMethodId(e.target.value)}
          className='border rounded-md px-3 py-2 text-sm min-w-[180px]'
          aria-label='Método de evaluación'
        >
          <option value=''>Método (auto)</option>
          {methods.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
        <select
          value={orgUnitId}
          onChange={(e) => setOrgUnitId(e.target.value)}
          className='border rounded-md px-3 py-2 text-sm min-w-[160px]'
          aria-label='Nivel organizativo'
        >
          <option value=''>Toda la estructura</option>
          {flatUnits.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({u.kind})
            </option>
          ))}
        </select>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className='border rounded-md px-3 py-2 text-sm'
          aria-label='Tipo'
        >
          <option value=''>Todos los tipos</option>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {RISK_KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className='border rounded-md px-3 py-2 text-sm'
          aria-label='Estado'
        >
          <option value=''>Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {RISK_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <label className='flex items-center gap-2 text-sm px-2'>
          <input
            type='checkbox'
            checked={residual}
            onChange={(e) => setResidual(e.target.checked)}
          />
          Evaluación residual
        </label>
        <label className='flex items-center gap-2 text-sm'>
          Umbral PRM
          <input
            type='number'
            min={1}
            max={125}
            value={promotionThreshold}
            onChange={(e) => setPromotionThreshold(Number(e.target.value) || 50)}
            className='border rounded-md px-2 py-1 w-16 text-sm'
          />
        </label>
      </div>

      <EvaluationThresholdsPanel methods={methods} onUpdated={() => void load()} />

      {error && <p className='text-sm text-destructive'>{error}</p>}

      {loading && <p className='text-sm text-muted-foreground'>Cargando tablero…</p>}

      {!loading && data && (
        <>
          <section className='grid grid-cols-2 md:grid-cols-4 gap-4' data-testid='dashboard-kpis'>
            <KpiCard label='Altos abiertos' value={data.kpis.highOpenCount} />
            <KpiCard label='% con acción' value={`${data.kpis.withActionPercent}%`} />
            <KpiCard label='Acciones vencidas' value={data.kpis.overdueActionsCount} />
            <KpiCard
              label='Eficacia tratamiento'
              value={`${data.kpis.treatmentEffectivenessPercent}%`}
            />
          </section>

          {data.heatmap && (
            <section className='rounded-lg border p-4 bg-card'>
              <h2 className='text-sm font-semibold mb-3'>
                Mapa de calor {data.heatmap.residual ? '(residual)' : '(inicial)'}
              </h2>
              <RiskHeatmap
                cells={data.heatmap.cells}
                axisXLabel={data.heatmap.axisXLabel}
                axisYLabel={data.heatmap.axisYLabel}
                methodKind={data.heatmap.methodKind}
                thresholds={data.heatmap.thresholds}
              />
            </section>
          )}

          <section className='rounded-lg border p-4 bg-card'>
            <h2 className='text-sm font-semibold mb-3'>Riesgos por estructura (MAP-02)</h2>
            {data.structure.length === 0 ? (
              <p className='text-sm text-muted-foreground'>Sin nodos con riesgos.</p>
            ) : (
              <div className='space-y-3'>
                {data.structure.map((node) => (
                  <div key={node.orgUnitId} className='border rounded-md p-3'>
                    <div className='flex justify-between text-sm font-medium'>
                      <span>
                        {node.name}{' '}
                        <span className='text-muted-foreground font-normal'>({node.kind})</span>
                      </span>
                      <span className='text-muted-foreground'>
                        {node.riskCount} riesgos · {node.highCount} altos
                      </span>
                    </div>
                    {node.risks.length > 0 && (
                      <ul className='mt-2 text-xs space-y-1'>
                        {node.risks.map((r) => (
                          <li key={r.id}>
                            <Link to={`/riesgos/${r.id}`} className='text-primary hover:underline'>
                              {r.code}
                            </Link>{' '}
                            — {r.title}{' '}
                            {r.levelLabel && (
                              <span className='text-muted-foreground'>({r.levelLabel})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className='rounded-lg border p-4 bg-card'>
            <div className='flex items-center justify-between mb-3'>
              <h2 className='text-sm font-semibold'>Panel de contexto (MAP-04 / PRM-05)</h2>
              <Link to='/contexto' className='text-xs text-primary hover:underline'>
                Ir a contexto →
              </Link>
            </div>
            <div className='grid md:grid-cols-2 gap-4'>
              <div>
                <h3 className='text-xs font-medium text-muted-foreground mb-2'>
                  Candidatos a promover (≥ {data.context.promotionCandidates.threshold})
                </h3>
                {data.context.promotionCandidates.swot.length === 0 &&
                data.context.promotionCandidates.parties.length === 0 &&
                data.context.promotionCandidates.contextChanges.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>Sin candidatos sobre umbral.</p>
                ) : (
                  <ul className='text-sm space-y-2'>
                    {data.context.promotionCandidates.swot.map((s) => (
                      <li key={s.id} className='border rounded p-2'>
                        <span className='text-xs bg-muted px-1 rounded'>FODA</span> {s.description.slice(0, 80)}
                        {s.priorityIndex != null && (
                          <span className='ml-2 text-xs text-muted-foreground'>I={s.priorityIndex}</span>
                        )}
                      </li>
                    ))}
                    {data.context.promotionCandidates.parties.map((p) => (
                      <li key={p.id} className='border rounded p-2'>
                        <span className='text-xs bg-muted px-1 rounded'>Parte</span> {p.name}
                        {p.priorityIndex != null && (
                          <span className='ml-2 text-xs text-muted-foreground'>I={p.priorityIndex}</span>
                        )}
                      </li>
                    ))}
                    {data.context.promotionCandidates.contextChanges.map((c) => (
                      <li key={c.id} className='border rounded p-2'>
                        <span className='text-xs bg-muted px-1 rounded'>Contexto</span>{' '}
                        {c.situation.slice(0, 80)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className='text-xs font-medium text-muted-foreground mb-2'>Destacados FODA</h3>
                <ul className='text-sm space-y-1'>
                  {data.context.swotHighlights.slice(0, 5).map((s) => (
                    <li key={s.id} className='truncate'>
                      {s.description.slice(0, 60)}
                      {s.promotedRiskId ? ' ✓' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className='rounded-lg border p-4 bg-card'>
            <h2 className='text-sm font-semibold mb-3'>Comparativo inicial vs residual (MAP-05)</h2>
            <p className='text-xs text-muted-foreground mb-3'>
              {data.comparative.summary.reducedCount} de {data.comparative.summary.totalWithResidual}{' '}
              redujeron nivel · reducción media {data.comparative.summary.avgReductionPercent}%
            </p>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-muted/50'>
                  <tr>
                    <th className='text-left p-2'>Código</th>
                    <th className='text-left p-2'>Título</th>
                    <th className='text-left p-2'>Inicial</th>
                    <th className='text-left p-2'>Residual</th>
                    <th className='text-left p-2'>↓</th>
                  </tr>
                </thead>
                <tbody>
                  {data.comparative.items.slice(0, 30).map((item) => (
                    <tr key={item.riskId} className='border-t'>
                      <td className='p-2 font-mono text-xs'>{item.code}</td>
                      <td className='p-2'>
                        <Link to={`/riesgos/${item.riskId}`} className='text-primary hover:underline'>
                          {item.title}
                        </Link>
                      </td>
                      <td className='p-2'>
                        {item.initialValue} ({item.initialLabel})
                      </td>
                      <td className='p-2'>
                        {item.residualValue != null
                          ? `${item.residualValue} (${item.residualLabel ?? '—'})`
                          : '—'}
                      </td>
                      <td className='p-2'>{item.reduced ? '✓' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className='rounded-lg border bg-card p-4'>
      <p className='text-xs text-muted-foreground'>{label}</p>
      <p className='text-2xl font-bold mt-1'>{value}</p>
    </div>
  );
}

export default RiskDashboardPage;
